import BulbOutlined from '@ant-design/icons/BulbOutlined';
import ClockCircleOutlined from '@ant-design/icons/ClockCircleOutlined';
import DownOutlined from '@ant-design/icons/DownOutlined';
import SendOutlined from '@ant-design/icons/SendOutlined';
import ThunderboltOutlined from '@ant-design/icons/ThunderboltOutlined';
import App from 'antd/es/app';
import Dropdown from 'antd/es/dropdown';
import Typography from 'antd/es/typography';
import { type AxiosError } from 'axios';
import { useState } from 'react';

import {
  ActionButton,
  ButtonBar,
  ButtonGroup,
  InputContainer,
  InputWrapper,
  ModelMenuItemContent,
  ModelSelectorButton,
  StyledTextArea,
} from './MessageInput.styles';

import { useFocus } from '@utils/hooks';

type ModelTypes = 'basic' | 'standard' | 'thorough';

// Model options for the dropdown
const modelOptions: {
  key: ModelTypes;
  label: string;
  description: string;
  icon: React.ReactNode;
}[] = [
  {
    key: 'basic',
    label: 'Instant',
    description: 'Immediate responses with concise insights',
    icon: <ThunderboltOutlined />,
  },
  {
    key: 'standard',
    label: 'Focused',
    description: 'Intelligent, structured reasoning',
    icon: <ClockCircleOutlined />,
  },
  {
    key: 'thorough',
    label: 'Advanced',
    description: 'Extensive analysis and deeper intelligence',
    icon: <BulbOutlined />,
  },
];

const MessageInput = ({
  disabled,
  onSubmit,
  requestedModel,
  setRequestedModel,
}: {
  disabled: boolean;
  onSubmit: ({
    value,
    options,
    model,
  }: {
    value: string;
    options: { webSearch: boolean; deepResearch: boolean };
    model: ModelTypes;
  }) => Promise<void>;
  requestedModel: ModelTypes;
  setRequestedModel: (model: ModelTypes) => void;
}) => {
  const [value, setValue] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [inputRef, setInputFocus] = useFocus();
  const { message } = App.useApp();

  const onClickSend = () => {
    setIsSubmitting(true);
    onSubmit({
      value,
      options: {
        webSearch: false,
        deepResearch: false,
      },
      model: requestedModel,
    })
      .then(() => {
        setValue('');
      })
      .catch((e: AxiosError<{ error: string }>) => {
        const error = e.response?.data?.error;
        void message.error(
          `Error: ${error ?? 'Failed to generate message. Please try again later'}`,
        );
      })
      .finally(() => {
        setIsSubmitting(false);
        setInputFocus();
      });
  };

  return (
    <InputContainer>
      <InputWrapper>
        <StyledTextArea
          ref={inputRef}
          autoFocus
          tabIndex={0}
          autoSize={{ minRows: 1, maxRows: 3 }}
          placeholder="Type your message here..."
          disabled={disabled}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onPressEnter={(e) => {
            if (value.length > 0 && !e.shiftKey) {
              onClickSend();
              e.preventDefault();
            }
          }}
        />
        <ButtonBar>
          <ButtonGroup>
            <Dropdown
              menu={{
                items: modelOptions.map((model) => ({
                  key: model.key,
                  label: (
                    <ModelMenuItemContent>
                      <span
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.25rem',
                        }}
                      >
                        {model.icon}
                        <Typography.Text>{model.label}</Typography.Text>
                      </span>
                      <Typography.Text type="secondary">
                        {model.description}
                      </Typography.Text>
                    </ModelMenuItemContent>
                  ),
                  onClick: () => setRequestedModel(model.key),
                })),
              }}
              trigger={['click']}
            >
              <ModelSelectorButton>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                  }}
                >
                  {
                    modelOptions.find((model) => model.key === requestedModel)
                      ?.icon
                  }
                  <span>
                    {
                      modelOptions.find((model) => model.key === requestedModel)
                        ?.label
                    }
                  </span>
                  <DownOutlined style={{ fontSize: '12px' }} />
                </div>
              </ModelSelectorButton>
            </Dropdown>
          </ButtonGroup>
          <ActionButton
            type="primary"
            loading={isSubmitting}
            disabled={!value.length || disabled}
            onClick={() => {
              if (value.length > 0) {
                onClickSend();
              }
            }}
            icon={isSubmitting ? null : <SendOutlined />}
          >
            Send
          </ActionButton>
        </ButtonBar>
      </InputWrapper>
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <Typography.Text type="secondary" style={{ fontSize: '0.6rem' }}>
          The assistant can make mistakes. Check important info.
        </Typography.Text>
      </div>
    </InputContainer>
  );
};

export default MessageInput;
